/**
 * Écran 3 — Le mandat (PRD §14.1, BACKLOG H2) : la dizaine d'événements déclenchés par le moteur, présentés un par un.
 * Chaque réponse est un mini-budget : coût, financement (recettes / dette), et où elle mène le pays en fin de mandat
 * (aperçu = la partie rejouée par le moteur avec cette réponse).
 */
import { decisionCard, type OptionOutcome } from "../components/event-card.js";
import { majorityThreshold } from "../components/tangible.js";
import { escapeHtml as e, int, missionShort, num, pct, signed } from "../format.js";
import type { Store } from "../state.js";
import { electionYear, mandateYears, stepsHtml } from "./existe.js";
import { mandateBar } from "../components/brand.js";

export function programmeSummary(store: Store): string {
  const st = store.state;
  const ms = st.programme.measure_ids.map((id) => store.content.measures.find((m) => m.id === id)).filter((m): m is NonNullable<typeof m> => !!m);
  const fin = store.content.levers
    .filter((lv) => st.financing.revenue_raises[lv.id])
    .map((lv) => `${e(lv.label)} ${signed(st.financing.revenue_raises[lv.id]!, 0)} Md€`);
  if (st.financing.across_the_board_cut) fin.push(`rabot ${pct(st.financing.across_the_board_cut * 100, 0)}`);
  const variant = (id: string) => {
    const v = st.programme.variants?.[id];
    const m = store.content.measures.find((x) => x.id === id);
    return v ? ` (${e(m?.variants?.find((x) => x.id === v)?.label ?? v)})` : "";
  };
  return `<details class="more"><summary>Programme en vigueur (${int(ms.length)} mesure${ms.length > 1 ? "s" : ""}) et financement</summary>
    <p class="sm">${ms.map((m) => `<span class="pill">${e(m.label)}${variant(m.id)}</span>`).join("") || "Budget tendanciel."}</p>
    <p class="mut sm">Financement : ${fin.join(" · ") || "aucune recette nouvelle"}.</p></details>`;
}

/** Bandeau compact de l'année : déficit, dette, majorité, pouvoir d'achat (selon ce que fournit le moteur). */
function yearStrip(store: Store, kpis: Record<string, number>, year: number): string {
  const cells: string[] = [];
  if (kpis.deficit_pct_gdp !== undefined) cells.push(`<div>Déficit<b>${pct(Math.abs(kpis.deficit_pct_gdp))}</b></div>`);
  if (kpis.debt_pct_gdp !== undefined) cells.push(`<div>Dette<b>${pct(kpis.debt_pct_gdp, 0)}</b></div>`);
  if (kpis.parliament_majority !== undefined) cells.push(`<div>Majorité<b class="${kpis.parliament_majority > majorityThreshold(store) ? "" : "down"}">${num(kpis.parliament_majority, 0)} %</b></div>`);
  if (kpis.purchasing_power_index !== undefined) cells.push(`<div>Pouvoir d'achat<b>${num(kpis.purchasing_power_index, 1)}</b></div>`);
  else if (kpis.unemployment_pct !== undefined) cells.push(`<div>Chômage<b>${pct(kpis.unemployment_pct)}</b></div>`);
  return cells.length ? `<div class="strip"><span class="l">Situation ${year}</span>${cells.join("")}</div>` : "";
}

export function renderMandat(store: Store): string {
  const tl = store.timelineAll;
  if (tl.game_over && tl.years.length === 0) {
    return `<div class="card event"><h1>Censuré</h1><p>${e(tl.game_over.reason)}</p><p class="mut"><span class="pill warn">convention de jeu</span> La censure repose sur des paramètres de jeu non sourcés (capital politique, majorité). Active le mode réaliste (écran Résultat ou Hypothèses) pour la désactiver.</p>
      <div class="row"><button type="button" class="primary" data-action="reset">Rejouer</button><button type="button" class="sec" data-action="go-resultat">Voir le résultat</button></div></div>`;
  }
  const d = store.currentDecision;
  const endYear = tl.years[tl.years.length - 1]?.year;
  if (!d) {
    return `<div class="card">${stepsHtml(store, 3)}<h1>Mandat terminé</h1><p class="mut">Toutes les décisions sont prises${tl.game_over ? ` (mandat interrompu en ${tl.game_over.year})` : ""}. Place au bilan chiffré : tes résultats face à « si rien ne change » et au budget du gouvernement.</p>
      <button type="button" class="primary" data-action="go-resultat">Voir mon bilan ${electionYear(store)}</button></div><div class="card">${programmeSummary(store)}</div>`;
  }
  // Aperçu de chaque option : la partie rejouée par le moteur avec cette réponse (les suivantes par défaut).
  const outcomes: Record<string, OptionOutcome> = {};
  for (const o of d.event.options) {
    const t = store.simulateWith({ ...store.state.choices, [d.id]: o.id });
    const last = t.years[t.years.length - 1];
    outcomes[o.id] = { kpis: last?.kpis ?? {}, year: last?.year, gameOver: t.game_over?.reason ?? null };
  }
  const yearState = tl.years.find((y) => y.year === d.year);
  // Barre de mandat : une case par année de budget (base + 1 … base + mandate_years), remplie jusqu'à l'année de la crise.
  const base = store.content.baseline.meta.base_year;
  const years = Array.from({ length: Math.max(1, mandateYears(store)) }, (_, i) => base + 1 + i);
  const missionLabel = (id: string) => missionShort(store.content.baseline.state.missions.find((m) => m.id === id)?.label ?? id);
  return `<div class="card">${stepsHtml(store, 3)}
    ${mandateBar(years, d.year - base)}
    <p class="hint">Ton budget est voté. Décision ${int(d.index)} sur ${int(d.total)} : à chaque crise, choisis une réponse. Les crises suivantes prennent la réponse par défaut dans les aperçus.</p>
    ${yearState ? yearStrip(store, yearState.kpis, yearState.year) : ""}
  </div>
  ${decisionCard(d.event, d.year, { index: d.index, total: d.total, endYear }, outcomes, missionLabel)}
  <div class="card">${programmeSummary(store)}</div>`;
}
